import { CssBaseline } from '@mui/material';
import { createTheme, StyledEngineProvider, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import { ReactNode, useMemo } from 'react';
import { breakpoints } from './breakpoints';
import { useSettings } from './hooks/useSettings';
import { ComponentOverrides } from './overrides/componentOverrides';
import { palette } from './palette';
import shadows, { customShadows } from './shadows';
import shape from './shape';
import typography from './typography';
import PublicSans from './fonts/PublicSans-VariableFont_wght.ttf';
import PublicSansItalic from './fonts/PublicSans-Italic-VariableFont_wght.ttf';

type ThemeConfigProps = {
  children: ReactNode;
};

export function ThemeConfig({ children }: ThemeConfigProps) {
  const { themeMode, themeDirection } = useSettings();
  const isLight = themeMode === 'light';

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      palette: isLight ? { ...palette.light, mode: 'light' } : { ...palette.dark, mode: 'dark' },
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark
    }),
    [isLight, themeDirection]
  );

  const theme = createTheme(themeOptions);
  theme.components = {
    ...ComponentOverrides(theme),
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Public Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 100 900;
          src: url(${PublicSans}) format('truetype');
        }
        @font-face {
          font-family: 'Public Sans';
          font-style: italic;
          font-display: swap;
          font-weight: 100 900;
          src: url(${PublicSansItalic}) format('truetype');
        }
      `
    }
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
